<template>
  <main class="change-password-page">
    <AppNavBar :title="t('auth.changePassword')" />

    <section class="change-password-form">
      <label class="change-password-form__label">{{ t('auth.currentPassword') }}</label>
      <van-field
        v-model="currentPassword"
        class="auth-form-field change-password-form__field"
        :type="showCurrentPassword ? 'text' : 'password'"
        :placeholder="t('auth.enterCurrentPassword')"
      >
        <template #button>
          <button type="button" class="auth-eye-btn" @click="showCurrentPassword = !showCurrentPassword">
            <app-image :src="showCurrentPassword ? eyeOpenIcon : eyeCloseIcon" alt="" />
          </button>
        </template>
      </van-field>

      <label class="change-password-form__label">{{ t('auth.newPassword') }}</label>
      <van-field
        v-model="newPassword"
        class="auth-form-field change-password-form__field"
        :type="showNewPassword ? 'text' : 'password'"
        :placeholder="t('auth.enterNewPassword')"
      >
        <template #button>
          <button type="button" class="auth-eye-btn" @click="showNewPassword = !showNewPassword">
            <app-image :src="showNewPassword ? eyeOpenIcon : eyeCloseIcon" alt="" />
          </button>
        </template>
      </van-field>

      <label class="change-password-form__label">{{ t('auth.confirmNewPassword') }}</label>
      <van-field
        v-model="confirmPassword"
        class="auth-form-field change-password-form__field"
        :type="showNewPasswordAgain ? 'text' : 'password'"
        :placeholder="t('auth.enterNewPassword')"
      >
        <template #button>
          <button type="button" class="auth-eye-btn" @click="showNewPasswordAgain = !showNewPasswordAgain">
            <app-image :src="showNewPasswordAgain ? eyeOpenIcon : eyeCloseIcon" alt="" />
          </button>
        </template>
      </van-field>

      <label class="change-password-form__agree">
        <van-checkbox v-model="checked" icon-size="16px" checked-color="#2c6bff" />
        <span>
          {{ t('auth.passwordOnlySavedLocally1') }}
          <em>{{ t('auth.passwordOnlySavedLocally2') }}</em>
        </span>
      </label>

      <AuthThemeButton block class="change-password-form__submit" :loading="submitting" @click="submitChange">
        {{ t('common.confirm') }}
      </AuthThemeButton>
    </section>
  </main>
</template>

<script setup>
import { ref } from 'vue';
import { showFailToast, showSuccessToast } from 'vant';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { changePayPwd } from '@/api/auth';
import AppNavBar from '@/components/AppNavBar.vue';
import AuthThemeButton from '@/view/auth/components/AuthThemeButton.vue';
import { useProfileStore } from '@/stores/profile';
import { saveWalletPassword } from '@/utils/walletSession';
import eyeOpenIcon from '@/assets/images/eye_open_icon.png';
import eyeCloseIcon from '@/assets/images/eye_close_icon.png';

const { t } = useI18n();
const router = useRouter();
const profileStore = useProfileStore();

const currentPassword = ref('');
const newPassword = ref('');
const confirmPassword = ref('');
const checked = ref(false);
const submitting = ref(false);
const showCurrentPassword = ref(false);
const showNewPassword = ref(false);
const showNewPasswordAgain = ref(false);

async function submitChange() {
  if (!currentPassword.value.trim()) {
    showFailToast(t('auth.enterCurrentPassword'));
    return;
  }
  if (newPassword.value.trim().length < 8) {
    showFailToast(t('auth.setNewPasswordMin8'));
    return;
  }
  if (newPassword.value.trim() === currentPassword.value.trim()) {
    showFailToast(t('auth.newPasswordSameAsOld'));
    return;
  }
  if (newPassword.value.trim() !== confirmPassword.value.trim()) {
    showFailToast(t('auth.passwordMismatch'));
    return;
  }
  if (!checked.value) {
    showFailToast(t('auth.passwordOnlySavedLocally2'));
    return;
  }

  submitting.value = true;
  try {
    await changePayPwd({
      payPwd: newPassword.value.trim(),
      oldPwd: currentPassword.value.trim()
    });
    if (profileStore.accountStorageAddress) {
      saveWalletPassword(profileStore.accountStorageAddress, newPassword.value.trim());
    }
    showSuccessToast(t('auth.changeSuccess'));
    router.back();
  } catch (_) {
    // handled in interceptor
  } finally {
    submitting.value = false;
  }
}
</script>

<style scoped lang="scss">
@use '@/styles/auth-form.scss';

.change-password-page {
  min-height: var(--app-viewport-height);
  background: #fff;
}

.change-password-form {
  padding: 16px 14px 24px;
}

.change-password-form__label {
  display: block;
  margin-bottom: 10px;
  color: #222;
  font-size: 15px;
  line-height: 22px;
}

.change-password-form__field {
  margin-bottom: 20px;
}

.auth-eye-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 0;
  background: transparent;
}

.auth-eye-btn .app-image {
  width: 18px;
  height: 18px;
}

.change-password-form__agree {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-top: 12px;
}

.change-password-form__agree span {
  flex: 1;
  color: #666;
  font-size: 12px;
  line-height: 18px;
}

.change-password-form__agree em {
  color: #e90026;
  font-style: normal;
}

.change-password-form__submit {
  margin-top: 12px;
}
</style>
